import { GetServerSidePropsContext } from 'next';
import dynamic from 'next/dynamic';
import { useSearchParams } from 'next/navigation';
import { useTranslation } from 'next-i18next';
import { useEffect, useMemo, useRef } from 'react';
import ProductListCU from '@/components/categ/CategCU/ProductListCU';
import SelectedFilters from '@/components/categ/CategCU/SelectedFilters';
import SortMobile from '@/components/categ/filters/SortMobile';
import SortDesktop from '@/components/categ/SortDesktop';
import TopCategory from '@/components/categ/TopCategory';
import CategFilters from '@/components/categ/winebox/CategFilters';
import CategFooter from '@/components/categ/winebox/CategFooter';
import CategHeader from '@/components/categ/winebox/CategHeader';
import BreadcrumbContainer from '@/components/common/BreadcrumbContainer';
import JsonLd from '@/components/common/JsonLd';
import MetaTagsMillesima from '@/components/layout/MetaTagsMillesima';
import ModalAlertPrimeurInfos from '@/components/primeurAlert/ModalAlertPrimeurInfos';
import { useScreenSizeContext } from '@/components/ScreenSize.context';
import { NS } from '@/constants';
import { DEFAULT_PAGE, DEFAULT_SORT, FILTERS, PAGE_SIZE } from '@/constants/categ';
import { FULL_SCREEN_CONTAINER_MOBILE } from '@/constants/gui';
import { ATTRIBUTES } from '@/constants/product';
import { SEO_TYPES } from '@/constants/seo';
import { usePrimeurUser } from '@/hooks';
import { useVisibleProducts } from '@/hooks/useVisibleProducts';
import buildLinkedData from '@/linkedData/collection-lkd';
import { useAppDispatch, useAppSelector } from '@/redux';
import { setCategInfos } from '@/redux/slices/categ.slice';
import { hideFullScreenContainerMobile, hidePageProgress, showPageProgress } from '@/redux/slices/gui.slice';
import { selectStoreConf, selectStoreId } from '@/redux/slices/root.slice';
import { useServices } from '@/services/useServices';
import { isNote } from '@/utils/categUtils';
import { updateFilterString } from '@/utils/filterUtils';
import { getCurrentProductItem } from '@/utils/productUtils';
import { scrollToPageTop } from '@/utils/scroll';
import styles from './CategViewGrid.module.scss';
import { fetchCategProducts } from './fetchCategProducts';
import { useCategViewData } from './useCategViewData';

const Pagination = dynamic(() => import('@/components/categ/Pagination'));
Pagination.displayName = 'Pagination';
const FilterMobileLike = dynamic(() => import('@/components/categ/filters/FilterMobileLike'), { ssr: false });
FilterMobileLike.displayName = 'FilterMobileLike';

export const CategViewGrid = ({
	products,
	categ,
	categId,
	nbTotalRows,
	page,
	sort,
	topCategs,
	hashUserFromQuery,
	isSearchMode,
	searchSessionId,
}) => {
	const { t } = useTranslation([NS.COMMON, NS.CATEG, NS.CHECKOUT, NS.ALTER]);
	const { datalayerService, productsService, searchAnalyticsService } = useServices();
	const dispatch = useAppDispatch();
	const isMobileDevice = useScreenSizeContext().isMobileOrTablet;
	const isDesktop = useScreenSizeContext().isDesktop;

	const storeId = useAppSelector(selectStoreId);
	const storeConf = useAppSelector(selectStoreConf);
	const baseUrl = useAppSelector(({ root }) => root.baseUrl);
	const url = useAppSelector(({ root }) => root.storeConf.url);
	const isSortMobileVisible = useAppSelector(({ gui }) => gui.isSortMobileVisible);
	const isFilterMobileVisible = useAppSelector(({ gui }) => gui.isFilterMobileVisible);
	const alertPrimeurInfosModalVisible = useAppSelector(({ gui }) => gui.alertPrimeurInfosModalVisible);

	const descriptionRef = useRef<HTMLDivElement>();
	const query = useSearchParams();

	const pageSize = PAGE_SIZE.LIST_CU;
	const {
		viewData,
		setViewData,
		filterQuery,
		isSwitchDisable,
		setIsSwitchDisable,
		selectedFiltersInfos,
		topListingRef,
		isCUCategory,
		refreshView,
	} = useCategViewData({
		products,
		categ,
		topCategs,
		nbTotalRows,
		page,
		sort,
		categId,
		initialFilterQuery: query?.get('filter') || '',
	});

	const onlyCUChecked = filterQuery.includes('conditionnement:onlyCU');

	usePrimeurUser({ hashUserFromQuery });

	const visibilityTracking = useVisibleProducts(
		datalayerService,
		viewData.products || [],
		storeConf,
		viewData.categ,
		filterQuery,
		viewData.sort,
		undefined,
	);

	useEffect(() => {
		const computeFilter = async () => {
			if (filterQuery && filterQuery !== '') {
				await refreshView({
					page: viewData.page || 1,
					sort: viewData.sort,
					filterQuery,
					onlyCU: onlyCUChecked,
					keepExtraParams: true,
				});
			}
		};

		void computeFilter();
	}, []);

	useEffect(() => {
		scrollToPageTop();
	}, [page]);

	// Recopie côté client l'ID de session de recherche généré au prefetch SSR, afin que
	// les clics sur les résultats (logClick) soient rattachés à la même session que la requête.
	useEffect(() => {
		if (searchSessionId) {
			searchAnalyticsService.setCurrentSearchSessionId(searchSessionId);
		}
	}, [searchSessionId]);

	useEffect(() => {
		const isClient = typeof window !== 'undefined';
		const search = isClient ? window.location.search : '';
		setIsSwitchDisable(filterQuery?.includes(FILTERS.FORMAT) || search.includes(FILTERS.FORMAT) || categ.isSpirit);
	}, [filterQuery]);

	useEffect(() => {
		if (categ && typeof window !== 'undefined') {
			updateDatalayer();
		}
	}, []);

	const updateDatalayer = () => {
		datalayerService.updateCategDatalayer(
			products,
			filterQuery,
			sort,
			categ?.sortOrder,
			categ,
			false,
			storeConf.storeId,
			categ?.nbTotalRows,
			'',
		);
	};

	useEffect(() => {
		productsService.getFilters({ storeId, categId, filter: '' }).then((response) => {
			const dynamicKeys =
				categ?.isDynamic && categ.dynamicCriteria
					? new Set(categ.dynamicCriteria.split('|').map((f: string) => f.split(':')[0]))
					: new Set();
			const filters =
				dynamicKeys.size > 0
					? response.filters.filter((f: { nameId: string }) => !dynamicKeys.has(f.nameId))
					: response.filters;
			setViewData((prev) => ({ ...prev, filters }));
		});
	}, [categId]);

	const handleDeleteAll = async () => {
		await refreshView({ page: viewData.page || 1, sort: viewData.sort, filterQuery: '', onlyCU: onlyCUChecked });
	};

	const handleFilterChange = async (changes) => {
		const searchTerm = query?.get('searchTerm');

		if (searchTerm) {
			dispatch(showPageProgress());
			dispatch(hideFullScreenContainerMobile({ modalType: FULL_SCREEN_CONTAINER_MOBILE.FILTER }));
			setTimeout(() => dispatch(hidePageProgress()), 800);
			return;
		}

		let newFilter = changes.filterQuery ?? filterQuery;
		const sort = +(changes.sort ?? viewData.sort ?? 1);
		const page = +(changes.nameId ? 1 : (changes.page ?? viewData.page ?? 1));

		if (changes.nameId) {
			const { nameId, valueId, check, price, minPrice, maxPrice, multi } = changes;
			if (price || nameId === 'Price') {
				newFilter = updateFilterString(newFilter, 'minPrice', minPrice, !!minPrice || minPrice === 0);
				newFilter = updateFilterString(newFilter, 'maxPrice', maxPrice, !!maxPrice);
			} else if (isNote(nameId)) {
				newFilter = updateFilterString(newFilter, nameId, valueId, false);
				newFilter = updateFilterString(newFilter, nameId, valueId, true);
			} else {
				newFilter = updateFilterString(newFilter, nameId, valueId, check, multi);
			}
		}

		await refreshView({ page: page, sort: sort, filterQuery: newFilter, onlyCU: changes.onlyCU ?? onlyCUChecked });
	};

	const computeShouldHaveShadow = (element: HTMLDivElement) => {
		if (!element) {
			return;
		}
		const topOffset = parseInt(getComputedStyle(element).top, 10) || 0;

		const onScroll = () => {
			if (element.getBoundingClientRect().top <= topOffset) {
				element.classList.add('shadow');
				return;
			}
			element.classList.remove('shadow');
		};
		document.addEventListener('scroll', onScroll, { passive: true });
	};

	const currentItemDraft = (product) =>
		getCurrentProductItem(product, filterQuery, viewData.sort, viewData.categ, false, storeId, false, isCUCategory);

	// The listing only renders the products for which an item can be computed: the markup
	// must describe those, with the very item their card prices
	const displayedEntries = useMemo(
		() =>
			(viewData.products ?? []).map((product) => ({ product, item: currentItemDraft(product) })).filter(({ item }) => !!item),
		[viewData.products, viewData.sort, viewData.categ, filterQuery, storeId, isCUCategory],
	);
	const displayedProducts = useMemo(() => displayedEntries.map(({ product }) => product), [displayedEntries]);

	// Search result pages are not canonical, they have no list worth describing
	const json = useMemo(
		() => (isSearchMode ? '' : buildLinkedData(viewData.categ, displayedEntries, viewData.page, storeConf)),
		[viewData.categ, displayedEntries, viewData.page, storeConf, isSearchMode],
	);

	const showCUProducts = () => {
		const changes = { nameId: ATTRIBUTES.CONDITIONING, valueId: FILTERS.ONLY_CU };
		handleFilterChange(
			!onlyCUChecked ? { ...changes, onlyCU: true, check: true } : { ...changes, onlyCU: false, check: false },
		);
	};

	return (
		<>
			<MetaTagsMillesima
				searchMode={isSearchMode}
				categ={viewData.categ}
				nbTotalRows={viewData.nbTotalRows}
				page={viewData.page}
				pageSize={pageSize}
				originalUrl={baseUrl}
				isMobileDevice={isMobileDevice}
			/>

			<JsonLd data={json} />

			<div className={styles.container}>
				{isDesktop && (
					<BreadcrumbContainer
						homeLabel={t('breadcrumbHomeLabel')}
						{...viewData?.categ}
						baseUrl={isDesktop ? url : baseUrl}
					/>
				)}
				<CategHeader
					categ={viewData?.categ}
					descriptionRef={descriptionRef}
					isDesktop={isDesktop}
					isMobileDevice={isMobileDevice}
				/>
				<div className={styles['total-products']} ref={topListingRef}>
					<span>{t('category:results.total_plural', { count: viewData.nbTotalRows })}</span>
				</div>
			</div>

			<div className={styles['sticky-container']} ref={(e) => computeShouldHaveShadow(e)}>
				<div className={styles['filter-button-container']}>
					<CategFilters
						onFilterChange={handleFilterChange}
						deleteFilter={handleFilterChange}
						deleteAll={handleDeleteAll}
						selectedFiltersInfos={selectedFiltersInfos}
						filters={viewData.filters ?? []}
						onlyCUChecked={onlyCUChecked}
						showCUProducts={showCUProducts}
						isFromWB={false}
						isFromCU={isCUCategory}
						sort={viewData.sort}
						isSwitchDisable={isSwitchDisable}
					/>
					{isDesktop && (
						<div className={styles.sort}>
							<SortDesktop
								selectedSort={viewData.sort}
								onSortChange={(s) => handleFilterChange({ sort: s })}
								isProducerCategory={false}
								t={t}
							/>
						</div>
					)}
				</div>
			</div>
			<div className={styles.container}>
				{selectedFiltersInfos?.length > 0 && isDesktop && (
					<SelectedFilters
						selectedFiltersInfos={selectedFiltersInfos}
						handleDelete={handleFilterChange}
						handleDeleteAll={handleDeleteAll}
					/>
				)}
				<ProductListCU
					productList={displayedProducts}
					computeCurrentItem={currentItemDraft}
					espotPush={categ?.espotListingPush}
					visibilityTracking={visibilityTracking}
					isCategCu={categId === SEO_TYPES.CATEG_CU_PREFIX_ID}
					isSearchMode={isSearchMode}
				/>
				{viewData.nbTotalRows > pageSize && (
					<Pagination
						t={t}
						categ={viewData.categ}
						nbTotalRows={viewData.nbTotalRows}
						page={viewData.page}
						pageSize={pageSize}
						filterQuery={filterQuery}
						onChange={(page) => handleFilterChange({ page })}
						isMobile={!isDesktop}
						customClassName={styles.pagination}
					/>
				)}
				{topCategs && (
					<div className={styles['top-category']}>
						<TopCategory
							t={t}
							topCategory={topCategs}
							isMobileDevice={true}
							numberOfLinks={topCategs.length}
							customClassName={{ title: styles.title, list: styles.list }}
						/>
					</div>
				)}
				<CategFooter
					categ={viewData.categ}
					isDesktop={isDesktop}
					isMobileDevice={isMobileDevice}
					descriptionRef={descriptionRef}
				/>
				{!isDesktop && (
					<BreadcrumbContainer
						homeLabel={t('breadcrumbHomeLabel')}
						{...viewData.categ}
						baseUrl={isDesktop ? url : baseUrl}
					/>
				)}
			</div>
			{isFilterMobileVisible && viewData.filters && (
				<FilterMobileLike
					showCUProducts={showCUProducts}
					onlyCUChecked={onlyCUChecked}
					filters={viewData.filters}
					selectedFiltersInfos={selectedFiltersInfos}
					deleteAll={handleDeleteAll}
					onChange={handleFilterChange}
					onDelete={handleFilterChange}
				/>
			)}
			{isSortMobileVisible && (
				<SortMobile
					selectedSort={viewData.sort}
					onChange={(s) => handleFilterChange({ sort: s })}
					isProducerCategory={false}
				/>
			)}
			{alertPrimeurInfosModalVisible && <ModalAlertPrimeurInfos isOpen={alertPrimeurInfosModalVisible} />}
		</>
	);
};

export default CategViewGrid;

/**
 * Prefetch function  called on server side
 * @param {object} ctx The server side context
 * @param {object} store The server side application store
 * @param {object} props The seo page props
 * @return {object} Return an object containing the component props
 *    layoutOption : object containing the needed page layout (see _app component)
 *    namespaces : an array containing the needed translation namespaces
 */
export const prefetchView = async (ctx: GetServerSidePropsContext, { getState, dispatch, services }, props) => {
	const { query, resolvedUrl } = ctx;
	const {
		root: { baseUrl },
		cart: {
			content: { storeIdForPrice: storeIdForPriceInCart },
		},
		auth: { loggedUser },
		gui: { storeIdForPrice: storeIdForPriceInStore },
	} = getState();
	const params = new URLSearchParams(resolvedUrl.split('?')[1]);
	const { productsService, searchAnalyticsService } = services;
	const { storeId } = props;
	const storeIdForPrice = storeIdForPriceInStore || storeIdForPriceInCart;

	const { hashUser: hashUserFromQuery } = query;
	const normalize = (value, def) => (value ? +value : def);
	const sort = normalize(params.get('sort'), DEFAULT_SORT);
	const page = normalize(params.get('page'), DEFAULT_PAGE);
	const filterQuery = params.get('filter') || '';

	let categId;

	if (props?.id) {
		categId = props.id.includes(`-${storeId}`) ? props.id.replace(`-${storeId}`, '') : props.id;
	} else {
		const stateCateg = getState().categ;
		categId = stateCateg.categId;
	}

	const searchTerm = params.get('searchTerm') || undefined;
	const onlyCUChecked = filterQuery?.includes('conditionnement:onlyCU');
	const isCUCategory = categId?.includes(SEO_TYPES.CATEG_CU_PREFIX_ID);
	const isSearchMode = !!searchTerm;
	// Généré côté serveur pour la requête de recherche, puis propagé au client (voir props)
	// afin que les clics sur les résultats soient rattachés à cette même session.
	const searchSessionId = isSearchMode ? searchAnalyticsService?.startNewSearchSession() : undefined;

	const categInfos = await fetchCategProducts({
		productsService,
		storeId,
		categId,
		isCUCategory,
		onlyCU: onlyCUChecked,
		page,
		sort,
		filterQuery,
		searchTerm,
		storeIdForPrice,
		ec: !!loggedUser?.isEc,
		withFilter: filterQuery && filterQuery !== '' ? 'true' : 'false',
		searchSessionId,
	});

	dispatch(setCategInfos({ categId, content: categInfos, filterQuery }));
	const namespacesRequired = [NS.COMMON, NS.CATEG, NS.CHECKOUT, NS.ALTER];

	return {
		layoutOptions: {
			withScrollButton: true,
		},
		namespacesRequired,
		page,
		sort,
		isSearchMode,
		searchSessionId,
		originalUrl: baseUrl,
		products: categInfos?.products,
		categ: categInfos?.categ ?? categInfos,
		topCategs: categInfos?.top,
		categId: categId === categInfos?.categ?.groupIdentifier ? categId : categInfos?.categ?.groupIdentifier,
		nbTotalRows: categInfos?.nbTotalRows,
		hashUserFromQuery,
	};
};
